import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { defineMessages, FormattedMessage } from 'react-intl';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useApolloClient } from '@apollo/client';
import { actionSetMapTool } from '@tmapy/ui-manager';
import { INTERACTION, LAYER_IDS, MAP_TOOL, GEOMETRY_TYPE } from '@tmapy/config';
import { useMessage } from '@tmapy/intl';
import { useLocation } from '@tmapy/router';
import { QUERY_STATE } from '@tmapy/utils';
import { actionSetInteraction, actionInteractionSelectFeatures, actionInteractionModifyGeoJSONFeatures, } from '@tmapy/mapcore';
import { SvgPoint, SvgLine, SvgPolygon, SvgSelect, SvgText, BtnBar, FormItem, SecondaryBtn, Textarea, CtaBtn, DangerAlert, SuccessAlert, SecondaryLink, SIZE, } from '@tmapy/style-guide';
import { actionNoteDelete, actionNoteDeleteAll, actionSaveNotes, actionSetNotesState, } from '../actions';
import { ACTION_INTERACTION_MODIFY } from '../constants';
const msg = defineMessages({
    select: {
        id: 'notes.select',
        defaultMessage: 'Vybrat poznámku',
    },
    selectTooltip: {
        id: 'notes.select.tooltip',
        defaultMessage: 'Vybrat vytvořenou poznámku',
    },
    label: {
        id: 'notes.label',
        defaultMessage: 'Popisek',
    },
    save: {
        id: 'notes.save',
        defaultMessage: 'Uložit',
    },
    attributes: {
        id: 'notes.attributes',
        defaultMessage: 'Vlastnosti prvku',
    },
    saveQueryStateSuccess: {
        id: 'notes.saveQueryState.success',
        defaultMessage: 'Poznámka uložena.',
    },
    saveQueryStateFail: {
        id: 'notes.saveQueryState.fail',
        defaultMessage: 'Poznámku se nepodařilo uložit. Zkuste to prosím později.',
    },
    noteListLink: {
        id: 'notes.noteListLink',
        defaultMessage: 'Přehled poznámek',
    },
    clearAll: {
        id: 'notes.clearAll',
        defaultMessage: 'Smazat vše',
    },
    clear: {
        id: 'notes.clear',
        defaultMessage: 'Smazat prvek',
    },
    point: {
        id: 'notes.point',
        defaultMessage: 'Vytvořit bodovou poznámku',
    },
    line: {
        id: 'notes.line',
        defaultMessage: 'Vytvořit liniovou poznámku',
    },
    polygon: {
        id: 'notes.polygon',
        defaultMessage: 'Vytvořit polygonovou poznámku',
    },
    text: {
        id: 'notes.text',
        defaultMessage: 'Vytvořit textovou poznámku',
    },
});
export const Notes = React.memo(({ isActive, noteFeatures, interaction, editedFeatures, agendaName, noteListLink, dispatch }) => {
    const formatMessage = useMessage();
    const location = useLocation();
    const { saveQueryState, isChanged, currentNoteId } = useSelector((state) => state.notes);
    const activeMapTool = useSelector((state) => state.uiManager.activeMapTool);
    const client = useApolloClient();
    const schema = client.serverSchema;
    const isNoteListQueryAvailable = !!schema.getQueryType()?.getFields()['notes'];
    const isToolActive = isActive &&
        interaction.layerId === LAYER_IDS.NOTES_MODIFY &&
        interaction.type === INTERACTION.DRAW;
    const { drawGeometryType } = interaction;
    const handleSelectNoteClick = useCallback(() => {
        dispatch(actionSetMapTool(MAP_TOOL.NOTE_SELECT));
        dispatch(actionSetInteraction(ACTION_INTERACTION_MODIFY));
    }, [dispatch]);
    const handlePointNoteClick = useCallback(() => {
        if (isToolActive && drawGeometryType === GEOMETRY_TYPE.POINT) {
            dispatch(actionSetMapTool(MAP_TOOL.NOTE_SELECT));
            dispatch(actionSetInteraction(ACTION_INTERACTION_MODIFY));
        }
        else {
            dispatch(actionSetMapTool(MAP_TOOL.NOTE_DRAW_POINT));
            dispatch(actionSetInteraction({
                type: INTERACTION.DRAW,
                layerId: LAYER_IDS.NOTES_MODIFY,
                drawGeometryType: GEOMETRY_TYPE.POINT,
                style: 'NOTE.active',
            }));
            dispatch(actionInteractionSelectFeatures(LAYER_IDS.NOTES, []));
        }
    }, [dispatch, isToolActive, drawGeometryType]);
    const handleLineNoteClick = useCallback(() => {
        if (isToolActive && drawGeometryType === GEOMETRY_TYPE.LINE_STRING) {
            dispatch(actionSetMapTool(MAP_TOOL.NOTE_SELECT));
            dispatch(actionSetInteraction(ACTION_INTERACTION_MODIFY));
        }
        else {
            dispatch(actionSetMapTool(MAP_TOOL.NOTE_DRAW_LINE));
            dispatch(actionSetInteraction({
                type: INTERACTION.DRAW,
                layerId: LAYER_IDS.NOTES_MODIFY,
                drawGeometryType: GEOMETRY_TYPE.LINE_STRING,
                style: 'NOTE.active',
            }));
            dispatch(actionInteractionSelectFeatures(LAYER_IDS.NOTES, []));
        }
    }, [dispatch, isToolActive, drawGeometryType]);
    const handlePolygonNoteClick = useCallback(() => {
        if (isToolActive && drawGeometryType === GEOMETRY_TYPE.POLYGON) {
            dispatch(actionSetMapTool(MAP_TOOL.NOTE_SELECT));
            dispatch(actionSetInteraction(ACTION_INTERACTION_MODIFY));
        }
        else {
            dispatch(actionSetMapTool(MAP_TOOL.NOTE_DRAW_POLYGON));
            dispatch(actionSetInteraction({
                type: INTERACTION.DRAW,
                layerId: LAYER_IDS.NOTES_MODIFY,
                drawGeometryType: GEOMETRY_TYPE.POLYGON,
                style: 'NOTE.active',
            }));
            dispatch(actionInteractionSelectFeatures(LAYER_IDS.NOTES, []));
        }
    }, [dispatch, isToolActive, drawGeometryType]);
    const handleTextNoteClick = useCallback(() => {
        if (isToolActive && drawGeometryType === GEOMETRY_TYPE.TEXT) {
            dispatch(actionSetInteraction(ACTION_INTERACTION_MODIFY));
            dispatch(actionSetMapTool(MAP_TOOL.NOTE_SELECT));
        }
        else {
            dispatch(actionSetMapTool(MAP_TOOL.NOTE_DRAW_TEXT));
            dispatch(actionSetInteraction({
                type: INTERACTION.DRAW,
                layerId: LAYER_IDS.NOTES_MODIFY,
                drawGeometryType: GEOMETRY_TYPE.TEXT,
                style: 'NOTE.active',
            }));
            dispatch(actionInteractionSelectFeatures(LAYER_IDS.NOTES, []));
        }
    }, [dispatch, isToolActive, drawGeometryType]);
    const selectedFeature = useMemo(() => {
        if (editedFeatures.length !== 1)
            return null;
        return editedFeatures[0];
    }, [editedFeatures]);
    const handleFeatureLabelChange = useCallback((_, data) => {
        if (!selectedFeature)
            return;
        const layerId = interaction.type === INTERACTION.SELECT_MODIFY ? LAYER_IDS.NOTES : LAYER_IDS.NOTES_MODIFY;
        dispatch(actionInteractionModifyGeoJSONFeatures(layerId, [
            {
                ...selectedFeature,
                properties: {
                    ...selectedFeature.properties,
                    label: data.value,
                },
            },
        ]));
        dispatch(actionSetNotesState({ isChanged: true }));
    }, [dispatch, selectedFeature, interaction]);
    const labelTextArea = useRef(null);
    useEffect(() => {
        if (labelTextArea.current && selectedFeature?.geometry) {
            labelTextArea.current.focus();
        }
    }, [selectedFeature?.geometry]);
    const featuresToSave = useMemo(() => {
        let featuresToSave = [...noteFeatures];
        editedFeatures.forEach((editFeature) => {
            const editFeatureId = editFeature.id || editFeature.properties?.id;
            const exist = featuresToSave.find((feature) => {
                const featureId = feature.id || feature.properties?.id;
                return featureId === editFeatureId;
            });
            if (featuresToSave.length && exist) {
                featuresToSave = featuresToSave.reduce((acc, cur) => {
                    const currId = cur.id || cur.properties?.id;
                    if (currId === editFeatureId) {
                        acc.push(editFeature);
                    }
                    else {
                        acc.push(cur);
                    }
                    return acc;
                }, []);
            }
            else {
                featuresToSave = [...featuresToSave, ...editedFeatures];
            }
        });
        return featuresToSave;
    }, [noteFeatures, editedFeatures]);
    const handleSave = useCallback(() => {
        const note = {
            routeId: location.route?.id,
            url: window.location.href,
            features: featuresToSave,
            agendaName,
        };
        dispatch(actionSaveNotes(client, note));
    }, [agendaName, client, featuresToSave, location, dispatch]);
    const handleClearAll = useCallback(() => {
        if (!location)
            return;
        const hashParams = { ...location.hashParams, note: undefined };
        dispatch(actionNoteDeleteAll(hashParams));
    }, [dispatch, location]);
    const handleClear = useCallback(() => {
        if (!selectedFeature)
            return;
        if (featuresToSave.length === 1) {
            handleClearAll();
        }
        else {
            const selectedFeatureId = selectedFeature.id || selectedFeature.properties?.id;
            dispatch(actionNoteDelete([selectedFeatureId]));
        }
    }, [dispatch, featuresToSave.length, handleClearAll, selectedFeature]);
    return (_jsxs("div", { className: 'sg-u-vs-2', children: [_jsxs("section", { className: 'sg-a-d-f sg-a-fw-w sg-a-jc-s sg-a-cg-2 sg-a-rg-1', children: [_jsxs("div", { className: 'sg-a-d-f sg-a-jc-s sg-a-g-1', children: [_jsx(SecondaryBtn, { icon: { element: _jsx(SvgPoint, {}) }, tooltip: formatMessage(msg.point), isActive: isToolActive && interaction.drawGeometryType === GEOMETRY_TYPE.POINT, onClick: handlePointNoteClick }), _jsx(SecondaryBtn, { icon: { element: _jsx(SvgLine, {}) }, tooltip: formatMessage(msg.line), isActive: isToolActive && interaction.drawGeometryType === GEOMETRY_TYPE.LINE_STRING, onClick: handleLineNoteClick }), _jsx(SecondaryBtn, { icon: { element: _jsx(SvgPolygon, {}) }, tooltip: formatMessage(msg.polygon), isActive: isToolActive && interaction.drawGeometryType === GEOMETRY_TYPE.POLYGON, onClick: handlePolygonNoteClick }), _jsx(SecondaryBtn, { icon: { element: _jsx(SvgText, {}) }, tooltip: formatMessage(msg.text), isActive: isToolActive && interaction.drawGeometryType === GEOMETRY_TYPE.TEXT, onClick: handleTextNoteClick })] }), _jsx(SecondaryBtn, { icon: { element: _jsx(SvgSelect, {}) }, tooltip: formatMessage(msg.selectTooltip), isActive: activeMapTool === MAP_TOOL.NOTE_SELECT, onClick: handleSelectNoteClick, children: _jsx(FormattedMessage, { ...msg.select }) })] }), selectedFeature && (_jsxs("section", { className: 'sg-a-mt-2 sg-u-bt sg-a-pt-2 sg-u-vs-1', children: [_jsxs("header", { className: 'sg-a-d-f sg-a-jc-sb sg-a-ai-b sg-a-rg-2', children: [_jsx("h3", { className: 'sg-u-type-h3', children: _jsx(FormattedMessage, { ...msg.attributes }) }), _jsx(BtnBar, { children: _jsx(SecondaryBtn, { isDanger: true, size: SIZE.SML, onClick: handleClear, children: _jsx(FormattedMessage, { ...msg.clear }) }) })] }), _jsx(FormItem, { id: 'aria-map-notes-featureLabel', label: formatMessage(msg.label), children: _jsx(Textarea, { ref: labelTextArea, value: selectedFeature.properties?.label ?? '', name: 'featureLabel', onChange: handleFeatureLabelChange }) })] })), saveQueryState === QUERY_STATE.FAIL && (_jsx(DangerAlert, { children: _jsx(FormattedMessage, { ...msg.saveQueryStateFail }) })), saveQueryState === QUERY_STATE.SUCCESS && !isChanged && (_jsx(SuccessAlert, { children: _jsx(FormattedMessage, { ...msg.saveQueryStateSuccess }) })), _jsxs(BtnBar, { stretch: true, children: [featuresToSave.length > 0 && (_jsx(CtaBtn, { isDisabled: !isChanged, queryState: saveQueryState === QUERY_STATE.WAITING ? 'waiting' : undefined, onClick: handleSave, children: _jsx(FormattedMessage, { ...msg.save }) })), isNoteListQueryAvailable && noteListLink?.href && (_jsx(SecondaryLink, { ...noteListLink, children: _jsx(FormattedMessage, { ...msg.noteListLink }) })), (currentNoteId || featuresToSave.length > 0) && (_jsx(SecondaryBtn, { isDanger: true, onClick: handleClearAll, children: _jsx(FormattedMessage, { ...msg.clearAll }) }))] })] }));
});
//# sourceMappingURL=Notes.js.map